import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import SEO from "components/seo";
import FooterSeven from "layout/footers/footer-7";
import HeaderSix from "layout/headers/header-6";
import Wrapper from "layout/wrapper";

import ghar360Logo from "assets/img/Main Images/ghar360/Icon_with_name_01.png";
import heroImage from "assets/img/Main Images/ghar360/Living Room_Shot (7).png";
import walkthroughImage from "assets/img/Main Images/ghar360/Screenshot_20260506_004950_Ghar360.jpg";
import realTimeImage from "assets/img/Main Images/ghar360/Living Room_Shot (3).png";
import rawFurnishedImage from "assets/img/Main Images/ghar360/Living Room_Shot _Unfurnished.png";
import aiTourImage from "assets/img/Main Images/ghar360/Screenshot_20260506_005009_Ghar360.jpg";
import roomInfoImage from "assets/img/Main Images/ghar360/Screenshot_20260506_005037_Ghar360.jpg";
import balconyImage from "assets/img/Main Images/ghar360/Balcony_Shot (3).png";
import vastuImage from "assets/img/Main Images/ghar360/Lobby_Shot (2).png";

const problemCards = [
  "Buyers struggle to imagine spaces.",
  "Static images don't answer questions.",
  "Videos don't let buyers explore.",
  "Sample flats are expensive and time-consuming.",
];

const showcaseItems = [
  {
    title: "Real-time Walkthrough",
    image: realTimeImage,
    alt: "Ghar360 real-time apartment walkthrough",
  },
  {
    title: "Raw to Furnished",
    image: rawFurnishedImage,
    alt: "Ghar360 furnished apartment comparison",
  },
  {
    title: "AI Guided Tour",
    image: aiTourImage,
    alt: "Ghar360 AI guided tour preview",
  },
  {
    title: "Room Information",
    image: roomInfoImage,
    alt: "Ghar360 room information interface",
  },
  {
    title: "Balcony View",
    image: balconyImage,
    alt: "Ghar360 balcony view visualization",
  },
  {
    title: "Vastu Compass",
    image: vastuImage,
    alt: "Ghar360 vastu compass preview",
  },
];

const features = [
  "Interactive Walkthrough",
  "Raw to Furnished Comparison",
  "AI Guided Home Tour",
  "Room Information",
  "Vastu Compass",
  "Curtain Interaction",
  "Screenshot Sharing",
  "Floor-specific Balcony View",
];

const impactItems = [
  {
    value: "2-3x",
    label: "Higher Buyer Engagement",
    note: "Estimate based on interactive demo usage compared with passive walkthrough presentations.",
  },
  {
    value: "30-50%",
    label: "Less Time Explaining Floor Plans",
    note: "Observed range for guided sales conversations where buyers can explore rooms visually.",
  },
  {
    value: "Clarity",
    label: "Better Buyer Understanding",
    note: "Buyers can review layouts, scale, balcony direction, and furnishing choices before site visits.",
  },
  {
    value: "Lower",
    label: "Dependence on Sample Flats",
    note: "Teams can present a project-ready digital experience before physical spaces are available.",
  },
];

const builders = [
  "Impress buyers during presentations",
  "Explain layouts more easily",
  "Reduce repetitive site visits",
  "Stand out from competitors",
  "Showcase projects before construction",
];

const buyers = [
  "Explore every room",
  "Compare furnished and raw layouts",
  "Understand room sizes",
  "View actual balcony direction",
  "Make informed buying decisions",
];

const comparisonRows = [
  ["Brochure", "Interactive"],
  ["Images", "Real-time"],
  ["Walkthrough Video", "User-controlled"],
  ["360 Panorama", "Complete Freedom to Explore"],
  ["Sample Flat", "Available Before Construction"],
];

const Ghar360 = () => {
  const [activeShowcase, setActiveShowcase] = useState(0);
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const activeShowcaseItem = showcaseItems[activeShowcase];
  const demoVideoUrl = "https://www.youtube.com/embed/O5E7tqCXYmE?autoplay=1&rel=0";
  const shareTitle = "Ghar360 | The Future of Real Estate Sales";
  const shareDescription =
    "Transform property presentations with interactive digital walkthroughs that let buyers experience every detail, anytime and from anywhere.";
  const shareImage = "https://rionick.com/assets/images/ghar360-social-preview.png";

  const openShowcase = (index) => {
    setActiveShowcase(index);
    document
      .getElementById("ghar360-showcase")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  useEffect(() => {
    const upsertMeta = (selector, attribute, value, content) => {
      let meta = document.head.querySelector(selector);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attribute, value);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    document.title = shareTitle;
    upsertMeta('meta[name="description"]', "name", "description", shareDescription);
    upsertMeta('meta[property="og:title"]', "property", "og:title", shareTitle);
    upsertMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      shareDescription
    );
    upsertMeta('meta[property="og:image"]', "property", "og:image", shareImage);
    upsertMeta('meta[property="og:url"]', "property", "og:url", "https://rionick.com/ghar360");
    upsertMeta('meta[property="og:type"]', "property", "og:type", "website");
    upsertMeta('meta[property="og:site_name"]', "property", "og:site_name", "Ghar360");
    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", shareTitle);
    upsertMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      shareDescription
    );
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", shareImage);
  }, [shareDescription, shareImage, shareTitle]);

  return (
    <Wrapper>
      <SEO
        metaTitle={shareTitle}
        metaDesc={shareDescription}
        metaKeywords="Ghar360, interactive property walkthrough, real estate walkthrough, apartment visualization, virtual property tour"
        canonical="https://www.rionick.com/ghar360"
        pageTitle={shareTitle}
      />
      <HeaderSix
        top_bar={false}
        commonOffCanvas={true}
        headerClass="ghar360-header-white"
      />

      <main className="ghar360-page">
        <section className="ghar360-hero" id="top">
          <div className="ghar360-hero__bg">
            <img src={heroImage} alt="Interactive apartment walkthrough" />
          </div>
          <div className="ghar360-hero__overlay"></div>
          <div className="container">
            <div className="ghar360-hero__content">
              <img
                className="ghar360-hero__logo"
                src={ghar360Logo}
                alt="Ghar360"
              />
              <span className="ghar360-kicker">Ghar360 by Rionick Studios</span>
              <h1>Experience Properties Before They Exist</h1>
              <p>
                Interactive real-time property walkthroughs that help builders
                showcase apartments before construction is completed.
              </p>
              <div className="ghar360-actions">
                <Link className="ghar360-btn ghar360-btn--primary" to="/contact-us">
                  Request a Demo
                </Link>
                <button
                  type="button"
                  className="ghar360-btn ghar360-btn--ghost"
                  onClick={() => setIsDemoOpen(true)}
                >
                  Watch Demo
                </button>
              </div>
            </div>
            <div className="ghar360-hero__panel" aria-label="Ghar360 interactive controls preview">
              <span>Live Walkthrough</span>
              <ul>
                <li>
                  <button type="button" onClick={() => openShowcase(1)}>
                    Furnished
                  </button>
                </li>
                <li>
                  <button type="button" onClick={() => openShowcase(4)}>
                    Balcony View
                  </button>
                </li>
                <li>
                  <button type="button" onClick={() => openShowcase(2)}>
                    AI Tour
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section className="ghar360-section ghar360-problem">
          <div className="container">
            <div className="ghar360-section-title text-center">
              <span className="ghar360-kicker">The Problem</span>
              <h2>Selling an Unbuilt Property Isn't Easy</h2>
            </div>
            <div className="ghar360-problem__grid">
              {problemCards.map((item, index) => (
                <div className="ghar360-problem__card" key={item}>
                  <span>0{index + 1}</span>
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="ghar360-section ghar360-intro" id="ghar360-preview">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-xl-5 col-lg-6">
                <span className="ghar360-kicker">Introducing Ghar360</span>
                <h2>The Interactive Property Experience</h2>
                <p>
                  Instead of watching a video, buyers can actually walk through
                  the apartment, explore every room, compare interiors, and
                  understand the property before it's built.
                </p>
              </div>
              <div className="col-xl-7 col-lg-6">
                <button
                  type="button"
                  className="ghar360-video-card"
                  onClick={() => setIsDemoOpen(true)}
                  aria-label="Play Ghar360 product preview video"
                >
                  <img src={walkthroughImage} alt="Ghar360 walkthrough preview" />
                  <div className="ghar360-video-card__play">
                    <i className="fa-solid fa-play"></i>
                  </div>
                  <div className="ghar360-video-card__caption">
                    Product preview demo
                  </div>
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="ghar360-section ghar360-showcase" id="ghar360-showcase">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-7">
                <div className="ghar360-showcase__screen">
                  <img
                    src={activeShowcaseItem.image}
                    alt={activeShowcaseItem.alt}
                  />
                </div>
              </div>
              <div className="col-lg-5">
                <span className="ghar360-kicker">Interactive Experience Showcase</span>
                <h2>Give Buyers Control, Not Just a Presentation</h2>
                <p>
                  Ghar360 turns property marketing into an inspectable digital
                  experience with guided exploration and sales-ready context.
                </p>
                <div className="ghar360-showcase__chips">
                  {showcaseItems.map((item, index) => (
                    <button
                      type="button"
                      key={item.title}
                      className={activeShowcase === index ? "is-active" : ""}
                      onClick={() => setActiveShowcase(index)}
                      onMouseEnter={() => setActiveShowcase(index)}
                    >
                      {item.title}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ghar360-section ghar360-features">
          <div className="container">
            <div className="ghar360-section-title text-center">
              <span className="ghar360-kicker">Features</span>
              <h2>Built for Real Estate Sales Conversations</h2>
            </div>
            <div className="ghar360-features__grid">
              {features.map((feature) => (
                <div className="ghar360-feature-card" key={feature}>
                  <i className="fa-solid fa-check"></i>
                  <h3>{feature}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="ghar360-section ghar360-impact">
          <div className="container">
            <div className="ghar360-section-title text-center">
              <span className="ghar360-kicker">Business Impact</span>
              <h2>Why Interactive Property Experiences Matter</h2>
              <p>
                These numbers are shown as practical estimates from interactive
                sales workflows, not universal industry guarantees.
              </p>
            </div>
            <div className="ghar360-impact__grid">
              {impactItems.map((item) => (
                <div className="ghar360-impact__card" key={item.label}>
                  <strong>{item.value}</strong>
                  <h3>{item.label}</h3>
                  <p>{item.note}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="ghar360-section ghar360-benefits">
          <div className="container">
            <div className="ghar360-section-title text-center">
              <span className="ghar360-kicker">Who Benefits</span>
              <h2>Designed for Both Sales Teams and Buyers</h2>
            </div>
            <div className="row">
              <div className="col-lg-6">
                <div className="ghar360-benefit-card">
                  <h3>Builders &amp; Sales Teams</h3>
                  <ul>
                    {builders.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="ghar360-benefit-card ghar360-benefit-card--light">
                  <h3>Home Buyers</h3>
                  <ul>
                    {buyers.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ghar360-section ghar360-steps">
          <div className="container">
            <div className="ghar360-section-title text-center">
              <span className="ghar360-kicker">How It Works</span>
              <h2>From Floor Plan to Interactive Experience</h2>
            </div>
            <div className="ghar360-steps__grid">
              <div className="ghar360-step">
                <span>1</span>
                <p>Share your floor plans and project details.</p>
              </div>
              <div className="ghar360-step">
                <span>2</span>
                <p>We build a fully interactive digital property experience.</p>
              </div>
              <div className="ghar360-step">
                <span>3</span>
                <p>Share it with buyers and let them explore independently.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="ghar360-section ghar360-comparison">
          <div className="container">
            <div className="ghar360-section-title text-center">
              <span className="ghar360-kicker">Why Choose Ghar360</span>
              <h2>A More Complete Way to Present Property</h2>
            </div>
            <div className="ghar360-table">
              <div className="ghar360-table__head">
                <span>Traditional Marketing</span>
                <span>Ghar360</span>
              </div>
              {comparisonRows.map(([traditional, ghar360]) => (
                <div className="ghar360-table__row" key={traditional}>
                  <span>{traditional}</span>
                  <strong>
                    <i className="fa-solid fa-check"></i>
                    {ghar360}
                  </strong>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="ghar360-final-cta">
          <div className="container">
            <div className="ghar360-final-cta__inner">
              <span className="ghar360-kicker">Ready to Launch</span>
              <h2>Transform the Way You Sell Real Estate</h2>
              <p>
                Give your buyers an immersive experience that builds confidence
                before construction is complete.
              </p>
              <Link className="ghar360-btn ghar360-btn--primary" to="/contact-us">
                Request a Demo
              </Link>
            </div>
          </div>
        </section>
      </main>

      {isDemoOpen && (
        <div
          className="ghar360-video-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Ghar360 demo video"
        >
          <button
            type="button"
            className="ghar360-video-modal__close"
            onClick={() => setIsDemoOpen(false)}
            aria-label="Close demo video"
          >
            <span></span>
          </button>
          <div className="ghar360-video-modal__frame">
            <iframe
              src={demoVideoUrl}
              title="Ghar360 demo video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}

      <FooterSeven />
    </Wrapper>
  );
};

export default Ghar360;
